import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

export const announcementAudienceSchema = z.enum(['all', 'students', 'judges'])
export const announcementSchema = z.object({
  id: publicIdSchema,
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(20_000),
  audience: announcementAudienceSchema,
  publishedAt: utcTimestampSchema,
  authorDisplayName: z.string().nullable(),
  /** Only present in the management view (`announcement.manage`): when it was emailed and to how many students/judges. Never on public or participant reads. */
  emailSentAt: utcTimestampSchema.nullable().optional(),
  emailRecipientCount: z.number().int().min(0).nullable().optional(),
})
export type AnnouncementDto = z.infer<typeof announcementSchema>

export const createAnnouncementRequestSchema = z.strictObject({
  title: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(20_000),
  audience: announcementAudienceSchema,
})
export type CreateAnnouncementRequest = z.infer<typeof createAnnouncementRequestSchema>

export const updateAnnouncementRequestSchema = z
  .strictObject({
    title: z.string().trim().min(1).max(200).optional(),
    body: z.string().trim().min(1).max(20_000).optional(),
    audience: announcementAudienceSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'Provide at least one field.' })
export type UpdateAnnouncementRequest = z.infer<typeof updateAnnouncementRequestSchema>

// ---------------------------------------------------------------------------
// Announcement email (AC-368). Recipients are always resolved server-side from
// this filter; a client never supplies addresses or user ids.
// ---------------------------------------------------------------------------

export const announcementEmailRoleSchema = z.enum(['student', 'teacher', 'judge'])

export const announcementRecipientFilterSchema = z.strictObject({
  /** Which competition roles to email (at least one). */
  roles: z.array(announcementEmailRoleSchema).min(1).max(3),
  /** Students/teachers only: members of an active team in one of these categories. */
  categoryIds: z.array(publicIdSchema).max(50).optional(),
  /** Students/teachers only: members of a team taking part in one of these rounds (round 1: every active team; later rounds: teams advanced into it). */
  roundIds: z.array(publicIdSchema).max(10).optional(),
  /** Students/teachers only: whose registration answer for `fieldKey` is one of `values` (e.g. 參賽組別). */
  registrationAnswer: z
    .strictObject({
      fieldKey: z.string().regex(/^[A-Za-z0-9_]{1,64}$/),
      values: z.array(z.string().min(1).max(200)).min(1).max(50),
    })
    .optional(),
})
export type AnnouncementRecipientFilter = z.infer<typeof announcementRecipientFilterSchema>

export const announcementRecipientPreviewDtoSchema = z.strictObject({
  count: z.number().int().min(0),
  byRole: z.strictObject({
    student: z.number().int().min(0),
    teacher: z.number().int().min(0),
    judge: z.number().int().min(0),
  }),
  /** Most recipients a single send may have. */
  cap: z.number().int().min(1),
})
export type AnnouncementRecipientPreviewDto = z.infer<typeof announcementRecipientPreviewDtoSchema>

export const sendAnnouncementEmailRequestSchema = z.strictObject({
  filter: announcementRecipientFilterSchema,
  /** The count the organizer confirmed; the send is refused if the recipients changed since. */
  expectedRecipientCount: z.number().int().min(1),
  /** Required to email an announcement that was already emailed. */
  resend: z.boolean().default(false),
})
export type SendAnnouncementEmailRequest = z.infer<typeof sendAnnouncementEmailRequestSchema>

export const announcementEmailResultDtoSchema = z.strictObject({
  recipientCount: z.number().int().min(0),
  failedCount: z.number().int().min(0),
})
export type AnnouncementEmailResultDto = z.infer<typeof announcementEmailResultDtoSchema>

/** AC-370: content of an announcement email to preview or test-send (works on unsaved content). */
export const announcementEmailContentRequestSchema = z.strictObject({
  title: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(20_000),
})
export type AnnouncementEmailContentRequest = z.infer<typeof announcementEmailContentRequestSchema>

export const announcementEmailPreviewDtoSchema = z.strictObject({
  subject: z.string(),
  text: z.string(),
})
export type AnnouncementEmailPreviewDto = z.infer<typeof announcementEmailPreviewDtoSchema>
